import moment from 'moment';
import { jsPDF } from 'jspdf';
import {
  formatNumberDecimal,
  formatWithCommas,
  replaceCurrencyString,
  roundToNumericPrecision,
} from '../utils/libs';
import { formatDisplayDateTime } from '../utils/formatDisplayDateTime';
import type {
  DynamicTableParams,
  OrderProductItem,
  Payment_Information,
} from '../screens/checkout/components/Receipt';
import { generateReceiptHTML } from './receiptHtmlTemplate';
import { buildReceiptTotalsSummary } from './receiptTotals';
import { getOrderLineProductDisplayName } from '../utils/productDisplayName';

export { generateReceiptHTML };

function calculatePartialTotals(order: OrderProductItem[]): number {
  return order.reduce((total, item) => {
    const { bp_product_information } = item;
    if (bp_product_information.bp_product_price_type === 'unit') {
      return (
        total +
        bp_product_information.bp_product_selling_price *
          ((item.bp_order_mass ?? 0) - (item.bp_order_paid_mass ?? 0))
      );
    }
    const currentQuantity =
      item.bp_order_quantity - (item.bp_order_paid_quantity ?? 0);
    return (
      total + bp_product_information.bp_product_selling_price * currentQuantity
    );
  }, 0);
}

const getOrderLineDisplayName = (item: OrderProductItem): string =>
  getOrderLineProductDisplayName(item);

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildReceiptPdfBlob(
  params: DynamicTableParams,
  receiptId: string,
): Blob {
  const { receipt, business, employeedata, currency, totalsMode } = params;
  const currencyText = replaceCurrencyString('Ksh', currency);
  const doc = new jsPDF({
    unit: 'pt',
    format: [288, 640],
    orientation: 'portrait',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 20;
  const right = pageWidth - 20;
  const bottom = pageHeight - 32;
  let y = 28;

  const ensurePageSpace = (height: number) => {
    if (y + height <= bottom) return;
    doc.addPage([288, 640], 'portrait');
    y = 28;
  };

  const getWrappedLines = (
    text: unknown,
    fontSize: number,
    fontStyle: string,
    maxWidth: number,
  ): string[] => {
    doc.setFont('helvetica', fontStyle);
    doc.setFontSize(fontSize);
    return String(text ?? '')
      .split(/\r?\n/)
      .flatMap(line => doc.splitTextToSize(line.trim(), maxWidth) as string[])
      .filter(Boolean);
  };

  const drawAlignedText = (
    text: unknown,
    x: number,
    align: 'left' | 'center' | 'right',
    fontSize = 10,
    fontStyle = 'normal',
    maxWidth = right - left,
  ) => {
    const lines = getWrappedLines(text, fontSize, fontStyle, maxWidth);
    const lineHeight = fontSize + 4;
    ensurePageSpace(Math.max(lineHeight, lines.length * lineHeight));

    lines.forEach(line => {
      doc.text(line, x, y, { align });
      y += lineHeight;
    });
  };

  const centerText = (text: unknown, fontSize = 10, fontStyle = 'normal') => {
    drawAlignedText(text, pageWidth / 2, 'center', fontSize, fontStyle);
  };

  const drawRule = () => {
    ensurePageSpace(10);
    doc.setDrawColor(180);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(left, y, right, y);
    doc.setLineDashPattern([], 0);
    y += 10;
  };

  const name = (business?.name || '').trim();
  const desc = (business?.description || '').trim();
  const phone = employeedata?.bp_owner_phone_number || 'N/A';
  const employeeName = employeedata?.bp_employee_name || '';
  const date = formatDisplayDateTime(new Date());
  const order = receipt?.order || [];
  const pi: Payment_Information = receipt.Payment_Information;
  const totalAmount = calculatePartialTotals(order);

  centerText(name, 15, 'bold');
  if (desc) centerText(desc, 9);
  centerText(`Tel: ${phone}`, 9);
  if (receiptId) centerText(`Receipt: ${receiptId}`, 9);
  centerText(date, 9);
  drawRule();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('#', left, y);
  doc.text('Item', left + 14, y);
  doc.text('Qty', 160, y, { align: 'center' });
  doc.text('Price', 212, y, { align: 'right' });
  doc.text('Amount', right, y, { align: 'right' });
  y += 8;
  drawRule();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  order.forEach((item, idx) => {
    if (typeof item !== 'object' || !item.bp_product_information) return;

    const unitSuffix =
      item.bp_product_information.bp_product_price_type === 'unit'
        ? item.bp_product_information.bp_product_unit_of_mass || ''
        : '';
    const itemDesc = getOrderLineDisplayName(item);
    const currQuantity = roundToNumericPrecision(
      item.bp_product_information.bp_product_price_type === 'unit'
        ? (item.bp_order_mass ?? 0) - (item.bp_order_paid_mass ?? 0)
        : item.bp_order_quantity - (item.bp_order_paid_quantity ?? 0),
    );
    const price = item.bp_product_information.bp_product_selling_price;
    const amount = roundToNumericPrecision(price * currQuantity);
    const descriptionLines = getWrappedLines(itemDesc, 8, 'normal', 100);
    const rowHeight = Math.max(12, descriptionLines.length * 9);

    ensurePageSpace(rowHeight);

    doc.text(String(idx + 1), left, y);
    doc.text(descriptionLines, left + 14, y);
    doc.text(`${formatNumberDecimal(currQuantity)}${unitSuffix}`, 160, y, {
      align: 'center',
    });
    doc.text(String(formatWithCommas(price)), 212, y, { align: 'right' });
    doc.text(String(formatWithCommas(amount)), right, y, { align: 'right' });
    y += rowHeight;
  });

  y += 6;
  drawRule();

  const summary = buildReceiptTotalsSummary(
    pi,
    totalAmount,
    totalsMode ?? 'print',
  );

  doc.setFontSize(9);
  summary.rows.forEach(r => {
    const formatted =
      r.emphasis === 'change'
        ? formatWithCommas(r.amount, 0, true)
        : formatWithCommas(r.amount);
    doc.setFont('helvetica', r.strong ? 'bold' : 'normal');
    if (r.emphasis === 'balance') {
      doc.setTextColor(185, 28, 28);
    } else {
      doc.setTextColor(34, 34, 34);
    }
    doc.text(r.label, left, y);
    doc.text(`${currencyText} ${formatted}`, right, y, { align: 'right' });
    y += 13;
  });
  doc.setTextColor(34, 34, 34);

  const totalLineVal = formatWithCommas(summary.grandTotal);

  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(`Total: ${currencyText} ${totalLineVal}`, pageWidth / 2, y, {
    align: 'center',
  });
  y += 18;

  if (summary.isPartialLedger && summary.isPaidInFull) {
    doc.setFontSize(12);
    doc.setTextColor(22, 101, 52);
    doc.text('*** PAID IN FULL ***', pageWidth / 2, y, { align: 'center' });
    doc.setTextColor(34, 34, 34);
    y += 16;
  }

  drawRule();

  doc.setFont('helvetica', 'normal');
  centerText(`Served by: ${employeeName}`, 8);
  centerText('GOODS ONCE SOLD WILL NOT BE RE-ACCEPTED', 7);
  centerText(`Thank you for choosing ${name}!`, 8);
  centerText('BizPlus POS', 9, 'bold');

  return doc.output('blob');
}

export async function shareReceiptAsPDF(
  params: DynamicTableParams,
): Promise<void> {
  const receiptId = String(
    params.receipt?.ReceiptID ||
      (params.receipt as any)?.receiptID ||
      (params.receipt as any)?.bp_order_master_receipt_reference_id ||
      'receipt',
  ).trim();

  const fileName = `Receipt_${receiptId}_${moment().format('YYYYMMDD_HHmmss')}`;
  const pdfFileName = `${fileName}.pdf`;
  const pdfBlob = buildReceiptPdfBlob(params, receiptId);
  if (typeof File !== 'undefined') {
    const pdfFile = new File([pdfBlob], pdfFileName, {
      type: 'application/pdf',
    });

    if (navigator.canShare?.({ files: [pdfFile] })) {
      try {
        await navigator.share({
          files: [pdfFile],
        });
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        throw err;
      }
      return;
    }
  }

  downloadBlob(pdfBlob, pdfFileName);
}
